//src/pages/EditarCliente.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import ModalSemPermissao from "../components/ModalSemPermissao";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BadgeCheck,
  Building2,
  Clock,
  FileClock,
  LockKeyhole,
  Save,
  ShieldCheck,
  UserRound,
} from "lucide-react";

import "./EditarCliente.css";

import { validarSenhaFrontend } from "../utils/senhaUtils";

function EditarCliente() {
  const { id } = useParams();
  const navigate = useNavigate();

  const usuario = JSON.parse(localStorage.getItem("usuario"));

  const podeEditarClientes =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "clientes" && p.pode_editar);

  const [cliente, setCliente] = useState({
    nome: "",
    email: "",
    telefone: "",
    tipo_pessoa: "fisica",
    cpf_cnpj: "",
    cnpj_empresa: "",
    cidade: "",
    uf: "",
    status: "ativo",
    cep: "",
    nome_empresa: "",
    endereco_empresa: "",
    tipo_identificador_pj: "",
    identificador_conta_pj: "",
    data_nascimento: "",
    genero: "",
    nome_fantasia: "",
    responsavel_nome: "",
    responsavel_cpf: "",
    documento_validado: 0,
    perfil_atualizado: 0,
    forcar_atualizacao_perfil: 0,
    termo_pontos_aceito: 0,
    data_aceite_termo: "",
    ip_aceite_termo: "",
    user_agent_aceite: "",
    motivo_inativacao: "",
    data_inativacao: "",
    senha: "",
    confirmarSenha: "",
  });

  const [auditoria, setAuditoria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    carregarDados();
  }, [id]);

  const carregarDados = async () => {
    try {
      setLoading(true);

      const [clienteRes, auditoriaRes] = await Promise.all([
        axios.get(`/clientes/${id}`),
        axios.get(`/clientes/${id}/auditoria`),
      ]);

      const data = clienteRes.data;

      setCliente((prev) => ({
        ...prev,
        nome: data.nome || "",
        email: data.email || "",
        telefone: data.telefone || "",
        tipo_pessoa: data.tipo_pessoa || "fisica",
        cpf_cnpj: data.cpf_cnpj || "",
        cnpj_empresa: data.cnpj_empresa || "",
        cidade: data.cidade || "",
        uf: data.estado || "",
        status: data.status || "ativo",
        cep: data.cep || "",
        nome_empresa: data.empresa_razao_social || data.nome_empresa || "",
        endereco_empresa: data.endereco_empresa || "",
        tipo_identificador_pj: data.tipo_identificador_pj || "",
        identificador_conta_pj: data.identificador_conta_pj || "",
        data_nascimento: data.data_nascimento
          ? String(data.data_nascimento).slice(0, 10)
          : "",
        genero: data.genero || "",
        nome_fantasia: data.nome_fantasia || "",
        responsavel_nome: data.responsavel_nome || "",
        responsavel_cpf: data.responsavel_cpf || "",
        documento_validado: data.documento_validado || 0,
        perfil_atualizado: data.perfil_atualizado || 0,
        forcar_atualizacao_perfil: data.forcar_atualizacao_perfil || 0,
        termo_pontos_aceito: data.termo_pontos_aceito || 0,
        data_aceite_termo: data.data_aceite_termo || "",
        ip_aceite_termo: data.ip_aceite_termo || "",
        user_agent_aceite: data.user_agent_aceite || "",
        motivo_inativacao: data.motivo_inativacao || "",
        data_inativacao: data.data_inativacao || "",
        senha: "",
        confirmarSenha: "",
      }));

      setAuditoria(Array.isArray(auditoriaRes.data) ? auditoriaRes.data : []);
    } catch (err) {
      console.error("Erro ao carregar cliente:", err);
      setErro("Erro ao carregar dados do cliente.");
    } finally {
      setLoading(false);
    }
  };

  const perfilConfirmado = useMemo(() => {
    return (
      Number(cliente.perfil_atualizado) === 1 &&
      Number(cliente.documento_validado) === 1 &&
      Number(cliente.termo_pontos_aceito) === 1 &&
      Number(cliente.forcar_atualizacao_perfil) !== 1
    );
  }, [cliente]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setCliente((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const limparDocumento = (valor) => String(valor || "").replace(/\D/g, "");

  const formatarDataHora = (data) => {
    if (!data) return "—";

    try {
      return new Date(data).toLocaleString("pt-BR");
    } catch {
      return "—";
    }
  };

  const handleSalvar = async () => {
    setErro("");
    setMensagem("");

    try {
      if (cliente.senha || cliente.confirmarSenha) {
        if (!cliente.senha || !cliente.confirmarSenha) {
          setErro("Preencha os dois campos de senha.");
          return;
        }

        if (cliente.senha !== cliente.confirmarSenha) {
          setErro("As senhas não conferem.");
          return;
        }

        const validacaoSenha = validarSenhaFrontend(cliente.senha);

        if (!validacaoSenha.valida) {
          setErro(validacaoSenha.mensagem);
          return;
        }
      }

      if (cliente.status === "inativo" && !cliente.motivo_inativacao) {
        setErro("Informe o motivo da inativação.");
        return;
      }

      setSalvando(true);

      const payload = {
        nome: cliente.nome,
        email: cliente.email,
        telefone: cliente.telefone,
        tipo_pessoa: cliente.tipo_pessoa,
        cpf_cnpj: limparDocumento(cliente.cpf_cnpj),
        cnpj_empresa: limparDocumento(cliente.cnpj_empresa),
        cidade: cliente.cidade,
        uf: cliente.uf,
        status: cliente.status,
        motivo_inativacao:
          cliente.status === "inativo" ? cliente.motivo_inativacao : null,
      };

      if (cliente.senha) {
        payload.senha = cliente.senha;
      }

      await axios.put(`/clientes/${id}`, payload);

      setMensagem("Cliente atualizado com sucesso!");
      await carregarDados();
    } catch (err) {
      console.error("Erro ao salvar cliente:", err);
      setErro(err.response?.data?.error || "Erro ao salvar cliente.");
    } finally {
      setSalvando(false);
    }
  };

  const renderBadgePerfil = () => {
    if (perfilConfirmado) {
      return (
        <span className="editarCliente-badge ok">
          <ShieldCheck size={13} />
          Perfil confirmado
        </span>
      );
    }

    return (
      <span className="editarCliente-badge warn">
        <AlertTriangle size={13} />
        Pendente atualização
      </span>
    );
  };

  const renderBadgeDocumento = () => {
    if (Number(cliente.documento_validado) === 1) {
      return (
        <span className="editarCliente-badge ok">
          <BadgeCheck size={13} />
          Documento validado
        </span>
      );
    }

    return (
      <span className="editarCliente-badge warn">
        <FileClock size={13} />
        Documento pendente
      </span>
    );
  };

  if (!podeEditarClientes) {
    return <ModalSemPermissao onClose={() => navigate("/clientes")} />;
  }

  if (loading) {
    return <div className="editarCliente-page">Carregando cliente...</div>;
  }

  return (
    <div className="editarCliente-page">
      <div className="editarCliente-topbar">
        <div>
          <Link to="/clientes" className="editarCliente-back">
            <ArrowLeft size={15} />
            Voltar para clientes
          </Link>

          <h2>Editar Cliente</h2>
          <p>
            Atualize dados cadastrais, acompanhe a confirmação de perfil e
            consulte o histórico de auditoria.
          </p>
        </div>

        <div className="editarCliente-topActions">
          <button
            type="button"
            className="editarCliente-btn primary"
            onClick={handleSalvar}
            disabled={salvando}
          >
            <Save size={16} />
            {salvando ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </div>

      {erro && <div className="editarCliente-alert erro">{erro}</div>}
      {mensagem && <div className="editarCliente-alert sucesso">{mensagem}</div>}

      <div className="editarCliente-summary">
        <div className="editarCliente-identity">
          <div className="editarCliente-avatar">
            {cliente.nome?.charAt(0)?.toUpperCase() || "C"}
          </div>

          <div>
            <h3>{cliente.nome || "Cliente"}</h3>
            <span>{cliente.email}</span>
            <div className="editarCliente-badgeRow">
              <span
                className={`editarCliente-badge ${
                  cliente.tipo_pessoa === "juridica" ? "pj" : "pf"
                }`}
              >
                {cliente.tipo_pessoa === "juridica" ? (
                  <Building2 size={13} />
                ) : (
                  <UserRound size={13} />
                )}
                {cliente.tipo_pessoa === "juridica" ? "Pessoa Jurídica" : "Pessoa Física"}
              </span>

              {renderBadgePerfil()}
              {renderBadgeDocumento()}
            </div>
          </div>
        </div>

        <div className="editarCliente-meta">
          <div>
            <span>Último aceite</span>
            <strong>{formatarDataHora(cliente.data_aceite_termo)}</strong>
          </div>

          <div>
            <span>IP do aceite</span>
            <strong>{cliente.ip_aceite_termo || "—"}</strong>
          </div>

          <div>
            <span>Logs</span>
            <strong>{auditoria.length}</strong>
          </div>
        </div>
      </div>

      <div className="editarCliente-grid">
        <section className="editarCliente-card">
          <div className="editarCliente-cardHeader">
            <h3>Dados cadastrais</h3>
          </div>

          <div className="editarCliente-formGrid">
            <div className="editarCliente-field">
              <label>Nome / Razão social</label>
              <input name="nome" value={cliente.nome} onChange={handleChange} />
            </div>

            <div className="editarCliente-field">
              <label>E-mail</label>
              <input
                type="email"
                name="email"
                value={cliente.email}
                onChange={handleChange}
              />
            </div>

            <div className="editarCliente-field">
              <label>Telefone</label>
              <input
                name="telefone"
                value={cliente.telefone}
                onChange={handleChange}
              />
            </div>

            <div className="editarCliente-field">
              <label>Tipo de pessoa</label>
              <select
                name="tipo_pessoa"
                value={cliente.tipo_pessoa}
                onChange={handleChange}
              >
                <option value="fisica">Pessoa Física</option>
                <option value="juridica">Pessoa Jurídica</option>
              </select>
            </div>

            <div className="editarCliente-field">
              <label>{cliente.tipo_pessoa === "fisica" ? "CPF" : "CNPJ"}</label>
              <input
                name="cpf_cnpj"
                value={cliente.cpf_cnpj}
                onChange={handleChange}
              />
              <small>
                Alterações feitas pelo admin ficam registradas em auditoria.
              </small>
            </div>

            {cliente.tipo_pessoa === "fisica" && (
              <>
                <div className="editarCliente-field">
                  <label>Data de nascimento</label>
                  <input
                    type="date"
                    value={cliente.data_nascimento || ""}
                    disabled
                  />
                </div>

                <div className="editarCliente-field">
                  <label>Gênero</label>
                  <input value={cliente.genero || "—"} disabled />
                </div>
              </>
            )}

            {cliente.tipo_pessoa === "juridica" && (
              <>
                <div className="editarCliente-field">
                  <label>Identificador PJ</label>
                  <input value={cliente.identificador_conta_pj || "—"} disabled />
                </div>

                <div className="editarCliente-field">
                  <label>Tipo do identificador</label>
                  <input value={cliente.tipo_identificador_pj || "—"} disabled />
                </div>

                <div className="editarCliente-field">
                  <label>Responsável</label>
                  <input value={cliente.responsavel_nome || "—"} disabled />
                </div>

                <div className="editarCliente-field">
                  <label>CPF do responsável</label>
                  <input value={cliente.responsavel_cpf || "—"} disabled />
                </div>
              </>
            )}
          </div>
        </section>

        <section className="editarCliente-card">
          <div className="editarCliente-cardHeader">
            <h3>Empresa vinculada</h3>
          </div>

          <div className="editarCliente-formGrid">
            <div className="editarCliente-field">
              <label>CNPJ da empresa</label>
              <input
                name="cnpj_empresa"
                value={cliente.cnpj_empresa}
                onChange={handleChange}
              />
            </div>

            <div className="editarCliente-field">
              <label>Razão social</label>
              <input value={cliente.nome_empresa || "—"} disabled />
            </div>

            <div className="editarCliente-field">
              <label>Cidade</label>
              <input name="cidade" value={cliente.cidade} onChange={handleChange} />
            </div>

            <div className="editarCliente-field">
              <label>UF</label>
              <input
                name="uf"
                value={cliente.uf}
                maxLength={2}
                onChange={handleChange}
              />
            </div>

            <div className="editarCliente-field">
              <label>CEP</label>
              <input value={cliente.cep || "—"} disabled />
            </div>

            <div className="editarCliente-field wide">
              <label>Endereço da empresa</label>
              <input value={cliente.endereco_empresa || "—"} disabled />
            </div>
          </div>
        </section>

        <section className="editarCliente-card">
          <div className="editarCliente-cardHeader">
            <h3>Status e segurança</h3>
          </div>

          <div className="editarCliente-formGrid">
            <div className="editarCliente-field">
              <label>Status</label>
              <select name="status" value={cliente.status} onChange={handleChange}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>

            {cliente.status === "inativo" && (
              <div className="editarCliente-field wide">
                <label>Motivo da inativação</label>
                <textarea
                  name="motivo_inativacao"
                  value={cliente.motivo_inativacao}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Informe o motivo da inativação..."
                />
              </div>
            )}

            <div className="editarCliente-field">
              <label>Nova senha</label>

              <input
                type="password"
                name="senha"
                value={cliente.senha}
                onChange={handleChange}
                placeholder="Opcional"
                autoComplete="new-password"
              />

              <small>
                Deixe em branco para manter a senha atual.
                Se alterar, use pelo menos 8 caracteres, incluindo letras e números.
              </small>
            </div>

            <div className="editarCliente-field">
                <label>Confirmar nova senha</label>

                <input
                  type="password"
                  name="confirmarSenha"
                  value={cliente.confirmarSenha}
                  onChange={handleChange}
                  placeholder="Repita a nova senha"
                  autoComplete="new-password"
                />

                {cliente.confirmarSenha &&
                  cliente.senha !== cliente.confirmarSenha && (
                    <small>
                      As senhas ainda não conferem.
                    </small>
                  )}
              </div>
          </div>
        </section>

        <section className="editarCliente-card auditoria">
          <div className="editarCliente-cardHeader">
            <h3>Auditoria do cadastro</h3>
            <span>{auditoria.length} registro(s)</span>
          </div>

          {auditoria.length === 0 ? (
            <div className="editarCliente-emptyAudit">
              Nenhum log de auditoria encontrado para este cliente.
            </div>
          ) : (
            <div className="editarCliente-auditList">
              {auditoria.map((log) => (
                <div className="editarCliente-auditItem" key={log.id}>
                  <div className="editarCliente-auditIcon">
                    <Clock size={15} />
                  </div>

                  <div className="editarCliente-auditContent">
                    <div className="editarCliente-auditTop">
                      <strong>{log.origem || "alteracao"}</strong>
                      <span>{formatarDataHora(log.criado_em)}</span>
                    </div>

                    <p>{log.motivo || "Alteração registrada."}</p>

                    <div className="editarCliente-auditMeta">
                      <span>
                        Por:{" "}
                        <strong>
                          {log.alterado_por_nome ||
                            log.alterado_por_tipo ||
                            "sistema"}
                        </strong>
                      </span>
                      <span>IP: {log.ip_origem || "—"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="editarCliente-footerActions">
        <button
          type="button"
          className="editarCliente-btn secondary"
          onClick={() => navigate("/clientes")}
        >
          Cancelar
        </button>

        <button
          type="button"
          className="editarCliente-btn primary"
          onClick={handleSalvar}
          disabled={salvando}
        >
          <LockKeyhole size={16} />
          {salvando ? "Salvando..." : "Salvar com auditoria"}
        </button>
      </div>
    </div>
  );
}

export default EditarCliente;